import type React from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { ContentsProps } from "../../types";

const VisitAnalytics: React.FC<ContentsProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full space-y-6 p-4">
      <h2 className="text-xl font-semibold">
        Visit Analytics for {data?.visit_analytics.date_range.year}
      </h2>

      {/* Combined Chart */}
      <div className="h-[500px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data?.visit_analytics.monthly_data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month_name"
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="category_a_count"
              name="Category A"
              fill="#8884d8"
              stackId="stack"
            />
            <Bar
              dataKey="category_b_count"
              name="Category B"
              fill="#82ca9d"
              stackId="stack"
            />
            <Bar
              dataKey="category_c_count"
              name="Category C"
              fill="#ffc658"
              stackId="stack"
            />
            <Line
              type="monotone"
              dataKey="total_visits"
              name="Total Visits"
              stroke="#ff7300"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border bg-gray-50 p-4 shadow-sm">
          <h3 className="mb-2 text-lg font-medium">Total Statistics</h3>
          <div className="space-y-2">
            <p className="text-gray-700">
              Total Visits:{" "}
              <span className="font-semibold">
                {data?.visit_analytics.totals.total_visits.toLocaleString()}
              </span>
            </p>
            <p className="text-gray-700">
              Category A Visits:{" "}
              <span className="font-semibold">
                {data?.visit_analytics.totals.category_a_total.toLocaleString()}
              </span>
            </p>
            <p className="text-gray-700">
              Category B Visits:{" "}
              <span className="font-semibold">
                {data?.visit_analytics.totals.category_b_total.toLocaleString()}
              </span>
            </p>
            <p className="text-gray-700">
              Category C Visits:{" "}
              <span className="font-semibold">
                {data?.visit_analytics.totals.category_c_total.toLocaleString()}
              </span>
            </p>
          </div>
        </div>

        <div className="rounded-lg border bg-gray-50 p-4 shadow-sm">
          <h3 className="mb-2 text-lg font-medium">Key Insights</h3>
          <div className="space-y-2">
            <p className="text-gray-700">
              Active Months:{" "}
              <span className="font-semibold">
                {data?.visit_analytics.monthly_data
                  .filter((month) => month.total_visits > 0)
                  .map((month) => month.month_name)
                  .join(", ")}
              </span>
            </p>
            <p className="text-gray-700">
              Peak Month:{" "}
              <span className="font-semibold">
                {
                  data?.visit_analytics.monthly_data.reduce((max, month) =>
                    month.total_visits > max.total_visits ? month : max
                  ).month_name
                }
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitAnalytics;
